/**
 * 벽 fbm 황금빛 셰이더 + 명화 모핑 + 면별 시드 + 글로벌 페이드.
 *
 * 황금 추상장(fbm)은 그대로 유지하고, uMorph(0→1)에 따라 명화 텍스처가
 * fbm 값을 마스크 삼아 "패치별로 응결되듯" 피어난다. uSeed로 면마다 다른 패턴을,
 * uFade로 피날레 후 전체 암전을 처리한다. 머티리얼 한 장 / 드로우 한 번.
 *
 * 팔레트/속도/모핑 상수는 config에서 받아 GLSL 리터럴로 주입한다.
 * ShaderMaterial이 projectionMatrix/modelViewMatrix/position/uv를 자동 주입.
 */
import type { WALL } from '../config';

export interface WallShaderOptions {
  palette: typeof WALL.palette;
  timeScale: number;
  /** 명화 dissolve 경계 폭. */
  morphSoftness: number;
  /** 면당 명화 반복 수 (UV 스케일). */
  uvRepeat: number;
}

/** 정수도 GLSL float 리터럴이 되도록 소수점을 보장한다 (예: 1 → "1.0"). */
function glslFloat(n: number): string {
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
}

function glslVec3(rgb: readonly number[]): string {
  return `vec3(${rgb.map(glslFloat).join(', ')})`;
}

export interface WallShaderSource {
  vertexShader: string;
  fragmentShader: string;
}

export function buildWallShader(opts: WallShaderOptions): WallShaderSource {
  const { palette, timeScale, morphSoftness, uvRepeat } = opts;
  const soft = glslFloat(morphSoftness);

  const vertexShader = /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const fragmentShader = /* glsl */ `
    precision highp float;
    varying vec2 vUv;
    uniform float uTime;
    uniform float uMorph;       // 0 = 황금 추상, 1 = 명화
    uniform float uHasPainting; // 텍스처 로드 전엔 0
    uniform vec2  uSeed;        // 면마다 다른 노이즈 패턴
    uniform float uFade;        // 1 = 정상, 0 = 암전(검정)
    uniform sampler2D uPainting;

    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453123); }
    float noise(vec2 p){
      vec2 i=floor(p), f=fract(p);
      float a=hash(i), b=hash(i+vec2(1.,0.)), c=hash(i+vec2(0.,1.)), d=hash(i+vec2(1.,1.));
      vec2 u=f*f*(3.0-2.0*f);
      return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
    }
    float fbm(vec2 p){ float v=0.0, amp=0.55; for(int i=0;i<5;i++){ v+=amp*noise(p); p*=2.0; amp*=0.5; } return v; }

    void main(){
      float t = uTime * ${glslFloat(timeScale)};
      float n  = fbm(vUv*3.0 + uSeed + vec2(t, -t*0.5));
      float n2 = fbm(vUv*6.5 + uSeed + vec2(-t*0.7, t));
      float v = n*0.6 + n2*0.4;

      // 황금 추상장
      vec3 deep  = ${glslVec3(palette.deep)};
      vec3 amber = ${glslVec3(palette.amber)};
      vec3 gold  = ${glslVec3(palette.gold)};
      vec3 goldCol = mix(deep, amber, smoothstep(0.28, 0.68, v));
      goldCol = mix(goldCol, gold, smoothstep(0.6, 0.95, v) * pow(v, 2.0));

      // 명화 리빌: fbm 값을 per-pixel 임계값으로 써서 그림이 패치별로 응결되듯 피어남.
      float threshold = clamp(v, 0.0, 1.0);
      float mm = mix(-${soft}, 1.0 + ${soft}, uMorph);
      float reveal = smoothstep(threshold - ${soft}, threshold + ${soft}, mm) * uHasPainting;
      vec3 paintingCol = texture2D(uPainting, vUv * ${glslFloat(uvRepeat)}).rgb;

      vec3 col = mix(goldCol, paintingCol, reveal);

      // 빛 알갱이 반짝임 유지(명화 위에선 약화) — 정적 사진이 아니라 '빛' 느낌.
      float fleck = smoothstep(0.86, 0.91, noise(vUv*42.0 + uSeed + t*3.0));
      col += gold * fleck * 0.5 * (1.0 - 0.6 * reveal);

      gl_FragColor = vec4(col * uFade, 1.0);
    }
  `;

  return { vertexShader, fragmentShader };
}
